import type { DataOf } from '@elara/firebolt/shared';

import { z } from 'zod';

import { collectionPath, defineModelSpec } from '@elara/firebolt/shared';
import { zFirestampOptional } from '@elara/firebolt/zod-types';

import { AddressSchema } from './schemas/shared-schemas.js';
import { AdditionalTravellerSchema, TravelerProfileSchema } from './schemas/TravelerProfileSchema.js';
import { id } from './schemas/zod-types.js';

import { MembershipSubscriptionSchema } from '../subscription.js';

/**
 * Auto-maintained fields used by the admin panel for fast list queries.
 * NEVER write to these from client code.
 *
 * Counters are best-effort projections maintained via FieldValue.increment and can drift
 * (see server/src/admin/admin-counters.ts). The schema deliberately does NOT enforce
 * `.nonnegative()` — a drifted counter should degrade the admin number, not take the
 * whole user document offline. Run `server/scripts/backfill-admin-counters.ts` to repair.
 */
export const UserProfileDerivedSchema = z.object({
  isStaff: z.boolean().optional().describe('Email ends with @elara.travel; populated at create / email change'),
  tripCount: z.number().int().optional().describe('Number of trips owned; maintained by worker (best-effort, may drift)'),
  chatCount: z.number().int().optional().describe('Number of chats owned; maintained by worker (best-effort, may drift)'),
  messageCount: z.number().int().optional().describe('Total messages sent; maintained by worker (best-effort, may drift)'),
  lastActiveAt: zFirestampOptional().describe('Timestamp of most recent message; maintained by worker. Seeded to createdAt on user create.'),
  platforms: z
    .array(z.string())
    .optional()
    .describe(
      'Client platforms this member has made an authenticated request from (see CLIENT_PLATFORMS: ' +
        'web / ios / android). Maintained server-side from the x-elara-client-platform header on flow ' +
        'requests (server/src/admin/user-platforms.ts) — never client-written. Typed as string[] rather ' +
        'than the enum deliberately: an unknown value should degrade the admin badge, not fail parsing ' +
        'of the whole user document. Read it through parseClientPlatform().',
    ),
  requiresPolicyAcceptance: z
    .boolean()
    .optional()
    .describe(
      'Whether the user must re-accept revised legal terms before sensitive actions. ' +
        'Best-effort denormalized projection of requiresPolicyReacceptance(); set false at create/accept ' +
        'and lazily re-derived by the server policy gate on the next sensitive action after a policy-version bump. ' +
        'The gate recomputes authoritatively from acceptedTermsVersion/acceptedPrivacyVersion — never trust this field for enforcement.',
    ),
});
export type UserProfileDerived = z.infer<typeof UserProfileDerivedSchema>;

/**
 * The canonical `ModelSpec` for the user profiles collection.
 * This spec defines the path (`users/{id}`), the data shape, and is the single source of truth for the user profile model.
 */
export const UserProfileSpec = defineModelSpec({
  path: collectionPath`users/${id}`,
  shape: {
    displayName: z.string().min(1, 'Display name is required'),
    email: z.string().email('Invalid email address'),
    photoURL: z.string().nullish(),
    hasCustomAvatar: z
      .boolean()
      .optional()
      .describe(
        'True when the user has taken explicit control of their avatar — uploaded a custom image OR ' +
          'removed their photo. Signals the client photoURL-sync monitor to stop overwriting photoURL ' +
          'with the auth provider (Google) photo, so the user\'s choice (a custom image, or an ' +
          'intentionally cleared photo) survives re-logins. Absent/false means photoURL tracks the ' +
          'provider photo (the default). The remove/change UI keys off photoURL, not this flag.',
      ),
    attributes: z.record(z.string(), z.string()).optional(),
    travelerProfile: TravelerProfileSchema.optional(),
    travellers: z.array(AdditionalTravellerSchema).optional(),
    onboardingCompletedAt: z.string().optional(),
    productTourCompletedAt: z.string().optional(),
    mcpKey: z.string().optional(),
    referralCode: z.string().optional(),
    phoneNumber: z.string().optional(),
    address: AddressSchema.nullish(),
    acceptedTermsAt: z.string().optional(),
    acceptedTermsVersion: z.string().optional(),
    acceptedPrivacyAt: z.string().optional(),
    acceptedPrivacyVersion: z.string().optional(),

    stripeCustomerId: z.string().optional().describe('Stripe Customer id (`cus_…`); server-authoritative'),
    subscription: MembershipSubscriptionSchema.optional().describe(
      'Billing state; server-authoritative. Absent = billing not applicable yet; read via membershipAccess()',
    ),

    deletionRequestedAt: z
      .string()
      .optional()
      .describe(
        'ISO timestamp set when the member requests account deletion (App Store requirement). ' +
          'Server-authoritative: written only by RequestAccountDeletionFlow and cleared implicitly when ' +
          'an admin hard-deletes the account. Never client-written (omitted from UpdateUserProfileSpec). ' +
          'Presence marks the account as pending deletion; drives the in-app pending banner.',
      ),

    smsWelcomeSentAt: z
      .string()
      .optional()
      .describe('ISO timestamp of the SMS welcome text. Server-only; prevents a second send.'),

    /** @deprecated Use travelerProfile.preferences instead */
    aboutMe: z.string().optional(),
    /** @deprecated Use travelerProfile.preferences instead */
    preferences: z.string().optional(),

    derived: UserProfileDerivedSchema.optional().describe('Auto-maintained admin-list fields. Never write directly.'),
  },
});

export type UserProfileSpec = typeof UserProfileSpec;

/** The TypeScript type representing the data of a user profile document after being parsed by Zod. */
export interface UserProfileData extends DataOf<UserProfileSpec> {}
